import { useState, useRef, useEffect, useCallback, Fragment } from "react";
import api from "../../api";
import "../../CSS/Rolemaster.css";
//
/* ════════ ACTIONS ════════ */
const ACTIONS = [
  { key: "view", label: "View" },
  { key: "add", label: "Add" },
  { key: "edit", label: "Edit" },
  { key: "delete", label: "Delete" },
  { key: "export", label: "Print/Export" },
];

/* Fallback emoji per module — the DB doesn't (yet) store a per-role icon,
   and Module.icons_name is a FontAwesome class rather than an emoji, so
   this is just a friendly visual fallback keyed by your real module names. */
const MOD_ICON = {
  System: "⚙️", Master: "🗂️", Operations: "🔄", Expense: "💰", Reports: "📈",
};
const iconForModule = (name) => MOD_ICON[name] || "📁";
const iconForRole = (role) => (role && role.isSystem ? "👑" : "🛡️");

/* ════════ HELPERS ════════ */
function allItems(menuTree) {
  const out = [];
  menuTree.forEach((m) => {
    m.items.forEach((it) => {
      out.push({
        moduleId: m.moduleId, moduleName: m.moduleName,
        submenuId: it.submenuId, submenuName: it.submenuName,
        pathname: it.pathname, isGroup: it.isGroup, actions: it.actions,
      });
      (it.children || []).forEach((ch) => {
        out.push({
          moduleId: m.moduleId, moduleName: m.moduleName,
          submenuId: ch.submenuId, submenuName: ch.submenuName,
          pathname: ch.pathname, isGroup: false, actions: ch.actions,
          parentId: it.submenuId,
        });
      });
    });
  });
  return out;
}
function buildEmptyRights(menuTree) {
  const r = {};
  allItems(menuTree).forEach((it) => {
    r[it.submenuId] = { view: false, add: false, edit: false, delete: false, export: false };
  });
  return r;
}
function mergeServerRights(menuTree, serverRights) {
  const r = buildEmptyRights(menuTree);
  Object.keys(serverRights || {}).forEach((k) => {
    if (k in r) {
      const c = serverRights[k] || {};
      r[k] = { view: !!c.view, add: !!c.add, edit: !!c.edit, delete: !!c.delete, export: !!c.export };
    }
  });
  return r;
}
function accessibleCount(menuTree, rights) {
  if (!rights) return 0;
  return allItems(menuTree).filter((it) => rights[it.submenuId] && rights[it.submenuId].view).length;
}
function errMsg(err, fallback) {
  const d = err && err.response && err.response.data;
  if (typeof d === "string" && d.trim()) return d;
  if (d && typeof d === "object" && d.message) return d.message;
  if (err && err.message) return err.message;
  return fallback;
}

/* ════════ ICONS ════════ */
const IcShield = (props) => (
  <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" {...props}><path d="M12 2l7 4v6c0 4-3 7-7 8-4-1-7-4-7-8V6z" /></svg>
);
const IcPlus = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" {...props}><path d="M12 5v14M5 12h14" /></svg>
);
const IcEdit = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
);
const IcTrash = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" /></svg>
);
const IcCheck = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}><polyline points="20 6 9 17 4 12" /></svg>
);
const IcSave = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v14a2 2 0 01-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>
);

/* ════════ MAIN COMPONENT ════════ */
export default function RoleMaster() {
  const [menuTree, setMenuTree] = useState([]);
  const [roles, setRoles] = useState([]);
  const [rightsByRole, setRightsByRole] = useState({}); // { [roleId]: {submenuId: {view,add,edit,delete,export}} }
  const [curRoleId, setCurRoleId] = useState(null);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [expandedFathers, setExpandedFathers] = useState(() => new Set());
  const [dirty, setDirty] = useState(false);
  const [toasts, setToasts] = useState([]);

  const [loadingShell, setLoadingShell] = useState(true);
  const [loadingRights, setLoadingRights] = useState(false);
  const [savingRights, setSavingRights] = useState(false);
  const [roleBusy, setRoleBusy] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editRoleId, setEditRoleId] = useState(null);
  const [formName, setFormName] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formActive, setFormActive] = useState(true);
  const [formCopyId, setFormCopyId] = useState("");
  const [nameErr, setNameErr] = useState(false);

  const curRoleMeta = roles.find((r) => r.roleId === curRoleId) || null;
  const curRights = curRoleId != null ? rightsByRole[curRoleId] : null;
  const curRole = curRoleMeta ? { ...curRoleMeta, rights: curRights } : null;

  /* ── TOASTS ── */
  function showToast(msg, type) {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 2700);
  }

  /* ── INITIAL LOAD ── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingShell(true);
      try {
        const [treeRes, rolesRes] = await Promise.all([
          api.get("/api/RoleMaster/menu-tree"),
          api.get("/api/RoleMaster/roles"),
        ]);
        if (cancelled) return;
        setMenuTree(treeRes.data || []);
        setRoles(rolesRes.data || []);
      } catch (err) {
        if (!cancelled) showToast(errMsg(err, "Failed to load Role Master."), "err");
      } finally {
        if (!cancelled) setLoadingShell(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── FETCH RIGHTS FOR A ROLE ── */
  const loadRights = useCallback(async (roleId) => {
    setLoadingRights(true);
    try {
      const res = await api.get(`/api/RoleMaster/rights/${roleId}`);
      setRightsByRole((prev) => ({ ...prev, [roleId]: mergeServerRights(menuTree, res.data.rights) }));
    } catch (err) {
      showToast(errMsg(err, "Failed to load rights for this role."), "err");
    } finally {
      setLoadingRights(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuTree]);

  /* ── ROLE SELECTION ── */
  function selectRole(id) {
    if (dirty && !window.confirm("You have unsaved changes. Discard them?")) return;
    setCurRoleId(id);
    setDirty(false);
    loadRights(id);
  }

  /* ── RIGHTS TOGGLE LOGIC (all local state; persisted on Save Rights) ── */
  function applyRight(rights, submenuId, action, val) {
    const cell = { ...rights[submenuId], [action]: val };
    if (action === "view" && !val) {
      ["add", "edit", "delete", "export"].forEach((a) => { if (a in cell) cell[a] = false; });
    }
    if (action !== "view" && val) {
      if ("view" in cell) cell.view = true;
    }
    return { ...rights, [submenuId]: cell };
  }

  function setCell(submenuId, action, val) {
    setRightsByRole((prev) => ({ ...prev, [curRoleId]: applyRight(prev[curRoleId], submenuId, action, val) }));
    setDirty(true);
  }

  function toggleColumn(moduleId, action) {
    const items = allItems(menuTree).filter((it) => it.moduleId === moduleId && it.actions.includes(action));
    const target = !items.every((it) => curRights[it.submenuId][action]);
    setRightsByRole((prev) => {
      let rights = prev[curRoleId];
      items.forEach((it) => { rights = applyRight(rights, it.submenuId, action, target); });
      return { ...prev, [curRoleId]: rights };
    });
    setDirty(true);
  }

  function toggleModule(moduleId, val) {
    const items = allItems(menuTree).filter((it) => it.moduleId === moduleId);
    setRightsByRole((prev) => {
      const rights = { ...prev[curRoleId] };
      items.forEach((it) => {
        const cell = { ...rights[it.submenuId] };
        it.actions.forEach((a) => { cell[a] = val; });
        rights[it.submenuId] = cell;
      });
      return { ...prev, [curRoleId]: rights };
    });
    setDirty(true);
  }

  function toggleCollapse(moduleId) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) next.delete(moduleId); else next.add(moduleId);
      return next;
    });
  }

  // Expands/collapses the "append panel" of grandchild rows under a father
  // row (e.g. Manifest -> Inscan/Outscan). Independent of module collapse.
  function toggleFatherExpand(submenuId) {
    setExpandedFathers((prev) => {
      const next = new Set(prev);
      if (next.has(submenuId)) next.delete(submenuId); else next.add(submenuId);
      return next;
    });
  }

  function preset(kind) {
    setRightsByRole((prev) => {
      const rights = {};
      allItems(menuTree).forEach((it) => {
        rights[it.submenuId] = {};
        it.actions.forEach((a) => {
          let v = false;
          if (kind === "all") v = true;
          else if (kind === "read") v = (a === "view" || a === "export");
          rights[it.submenuId][a] = v;
        });
      });
      return { ...prev, [curRoleId]: rights };
    });
    setDirty(true);
    showToast(kind === "all" ? "Full access granted" : kind === "read" ? "Read-only applied" : "All rights revoked", "info");
  }

  async function saveRights() {
    if (!curRoleId || !curRights) return;
    setSavingRights(true);
    try {
      const payload = {
        roleId: curRoleId,
        rights: Object.keys(curRights).map((submenuId) => ({
          submenuId: Number(submenuId),
          view: !!curRights[submenuId].view,
          add: !!curRights[submenuId].add,
          edit: !!curRights[submenuId].edit,
          delete: !!curRights[submenuId].delete,
          export: !!curRights[submenuId].export,
        })),
      };
      await api.post("/api/RoleMaster/rights", payload);
      setDirty(false);
      showToast("Menu rights saved for " + (curRoleMeta ? curRoleMeta.roleName : ""), "ok");
    } catch (err) {
      showToast(errMsg(err, "Failed to save rights."), "err");
    } finally {
      setSavingRights(false);
    }
  }

  /* ── ROLE MODAL ── */
  function openRoleModal(id) {
    setEditRoleId(id || null);
    const r = id ? roles.find((x) => x.roleId === id) : null;
    setFormName(r ? r.roleName : "");
    setFormDesc(r ? r.description || "" : "");
    setFormActive(r ? r.isActive : true);
    setFormCopyId("");
    setNameErr(false);
    setModalOpen(true);
  }
  function closeRoleModal() {
    if (roleBusy) return;
    setModalOpen(false);
  }
  async function saveRole() {
    const name = formName.trim();
    if (!name) { setNameErr(true); showToast("Enter a role name", "warn"); return; }
    setRoleBusy(true);
    try {
      if (editRoleId) {
        const res = await api.post(`/api/RoleMaster/roles/${editRoleId}`, {
          roleName: name,
          description: formDesc.trim(),
          isActive: formActive,
        });
        setRoles((prev) => prev.map((r) => (r.roleId === editRoleId ? { ...r, ...res.data } : r)));
        showToast("Role updated", "ok");
      } else {
        const res = await api.post("/api/RoleMaster/roles", {
          roleName: name,
          description: formDesc.trim(),
          isActive: formActive,
          copyFromRoleId: formCopyId ? Number(formCopyId) : null,
        });
        const newRole = res.data;
        setRoles((prev) => [...prev, newRole]);
        setCurRoleId(newRole.roleId);
        setDirty(false);
        loadRights(newRole.roleId);
        showToast('Role "' + name + '" created', "ok");
      }
      setModalOpen(false);
    } catch (err) {
      showToast(errMsg(err, "Failed to save role."), "err");
    } finally {
      setRoleBusy(false);
    }
  }
  async function delRole(id) {
    const r = roles.find((x) => x.roleId === id);
    if (!r) return;
    if (r.isSystem) { showToast("System roles cannot be deleted", "warn"); return; }
    if (!window.confirm('Delete role "' + r.roleName + '"? Users on this role will lose its rights.')) return;
    try {
      await api.post(`/api/RoleMaster/roles/${id}/delete`);
      setRoles((prev) => prev.filter((x) => x.roleId !== id));
      setRightsByRole((prev) => { const n = { ...prev }; delete n[id]; return n; });
      if (curRoleId === id) { setCurRoleId(null); setDirty(false); }
      showToast("Role deleted", "info");
    } catch (err) {
      showToast(errMsg(err, "Failed to delete role."), "err");
    }
  }

  const total = allItems(menuTree).length;
  const acc = curRights ? accessibleCount(menuTree, curRights) : 0;

  if (loadingShell) {
    return (
      <div className="rom-page">
        <div className="rom-page-inner">
          <div className="rom-empty">
            <div className="rom-e-ico">⏳</div>
            <div className="rom-e-t">Loading Role Master…</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rom-page">
      <div className="rom-bc">
        <span>Admin</span><span className="rom-bc-sep">›</span>
        <span>Security</span><span className="rom-bc-sep">›</span>
        <span className="rom-bc-cur">Role Master &amp; Menu Rights</span>
      </div>

      <div className="rom-page-inner">
        <div className="rom-pg-row">
          <div className="rom-pg-title">
            <h1>Role Master &amp; Menu Rights</h1>
            <p>Create roles and control exactly which menus each role can access and what actions they may perform.</p>
          </div>
        </div>

        <div className="rom-layout">
          {/* ROLES */}
          <div className="rom-card">
            <div className="rom-band rom-navy-band">
              <div className="rom-band-icon"><IcShield /></div>
              <div className="rom-band-title">Roles</div>
              <div className="rom-band-r"><span className="rom-band-badge">{roles.length}</span></div>
            </div>
            <div className="rom-add-role-bar">
              <button className="rom-btn rom-btn-primary rom-btn-block" onClick={() => openRoleModal(null)}>
                <IcPlus /> Create Role
              </button>
            </div>
            <div className="rom-role-list">
              {roles.map((r) => (
                <div
                  key={r.roleId}
                  className={`rom-role-item${curRoleId === r.roleId ? " rom-active" : ""}`}
                  onClick={() => selectRole(r.roleId)}
                >
                  <div className="rom-ri-ico" style={{ background: r.isSystem ? "var(--rom-purple-lt)" : "var(--rom-blue-lt)" }}>{iconForRole(r)}</div>
                  <div className="rom-ri-main">
                    <div className="rom-ri-name">{r.roleName}</div>
                    <div className="rom-ri-desc">{r.description || ""}</div>
                  </div>
                  <span className="rom-ri-users">{r.userCount}👤</span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHTS */}
          <div className="rom-card rom-rights-card">
            {!curRole ? (
              <div className="rom-empty">
                <div className="rom-e-ico">🔐</div>
                <div className="rom-e-t">Select a role</div>
                <div className="rom-e-s">Choose a role on the left to manage its menu rights</div>
              </div>
            ) : loadingRights || !curRights ? (
              <div className="rom-empty">
                <div className="rom-e-ico">⏳</div>
                <div className="rom-e-t">Loading rights…</div>
              </div>
            ) : (
              <>
                <div className={`rom-role-hd${dirty ? " rom-dirty" : ""}`}>
                  <div className="rom-rh-ico" style={{ background: curRole.isSystem ? "var(--rom-purple-lt)" : "var(--rom-blue-lt)" }}>{iconForRole(curRole)}</div>
                  <div>
                    <div className="rom-rh-name">
                      {curRole.roleName}
                      {curRole.isSystem && <span className="rom-pill rom-pill-sys">System</span>}
                      <span className="rom-dirty-dot" title="Unsaved changes"></span>
                    </div>
                    <div className="rom-rh-desc">{curRole.description || ""} · {curRole.userCount} user(s)</div>
                  </div>
                  <div className="rom-rh-r">
                    <span className={`rom-pill ${curRole.isActive ? "rom-pill-active" : "rom-pill-inactive"}`}>{curRole.isActive ? "Active" : "Inactive"}</span>
                    <button className="rom-ic-btn rom-ic-edit" onClick={() => openRoleModal(curRole.roleId)} title="Edit role"><IcEdit /></button>
                    {/* <button
                      className="rom-ic-btn rom-ic-del"
                      onClick={() => delRole(curRole.roleId)}
                      title="Delete role"
                      disabled={curRole.isSystem}
                      style={curRole.isSystem ? { opacity: .4, cursor: "not-allowed" } : undefined}
                    >
                      <IcTrash />
                    </button> */}
                  </div>
                </div>

                <div className="rom-toolbar">
                  <span className="rom-tb-lbl">Quick set:</span>
                  <button className="rom-btn rom-btn-ghost rom-btn-sm" onClick={() => preset("all")}>Grant Full Access</button>
                  <button className="rom-btn rom-btn-ghost rom-btn-sm" onClick={() => preset("read")}>Read-Only</button>
                  <button className="rom-btn rom-btn-ghost rom-btn-sm" onClick={() => preset("none")}>Revoke All</button>
                  <div className="rom-tb-spacer"></div>
                  <span className="rom-tb-lbl"><b>{acc}</b> / {total} menus accessible</span>
                  <button className="rom-btn rom-btn-green rom-btn-sm" onClick={saveRights} disabled={savingRights}>
                    <IcSave /> {savingRights ? "Saving…" : "Save Rights"}
                  </button>
                </div>

                <div className="rom-legend">
                  <span><b>View</b> = open screen</span>
                  <span><b>Add</b> = create</span>
                  <span><b>Edit</b> = modify</span>
                  <span><b>Delete</b> = remove</span>
                  <span><b>Print/Export</b> = print &amp; export</span>
                  <span style={{ color: "var(--rom-muted)" }}>· Unchecking View clears the row · checking any action grants View</span>
                </div>

                <div className="rom-rights-body">
                  {menuTree.map((m) => {
                    const isColl = collapsed.has(m.moduleId);
                    const modItems = allItems(menuTree).filter((x) => x.moduleId === m.moduleId);
                    const applicable = modItems.flatMap((it) => it.actions.map((a) => ({ it, a })));
                    const allOn = applicable.length > 0 && applicable.every(({ it, a }) => curRights[it.submenuId][a]);
                    const accInMod = modItems.filter((it) => curRights[it.submenuId].view).length;
                    return (
                      <div className="rom-mod-card" key={m.moduleId}>
                        <div className="rom-mod-head" onClick={() => toggleCollapse(m.moduleId)}>
                          <span className={`rom-mod-caret${isColl ? " rom-collapsed" : ""}`}>▼</span>
                          <span style={{ fontSize: 15 }}>{iconForModule(m.moduleName)}</span>
                          <span className="rom-mod-name">{m.moduleName}</span>
                          <span className="rom-mod-count">{accInMod}/{modItems.length}</span>
                          <label className="rom-mod-master" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={allOn}
                              onChange={(e) => toggleModule(m.moduleId, e.target.checked)}
                            /> All
                          </label>
                        </div>

                        {!isColl && (
                          <div className="rom-perm-scroll">
                            <table className="rom-perm-tbl">
                              <thead>
                                <tr>
                                  <th className="rom-menu">Menu</th>
                                  {ACTIONS.map((a) => (
                                    <th
                                      key={a.key}
                                      className="rom-act"
                                      onClick={() => toggleColumn(m.moduleId, a.key)}
                                      title={`Toggle ${a.label} for all ${m.moduleName}`}
                                    >
                                      {a.label}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {m.items.map((it) => {
                                  const hasChildren = it.isGroup && it.children && it.children.length > 0;
                                  const isExpanded = expandedFathers.has(it.submenuId);
                                  return (
                                    <Fragment key={it.submenuId}>
                                      <tr className={hasChildren && isExpanded ? "rom-parent-open" : ""}>
                                        <td className="rom-menu">
                                          {hasChildren ? (
                                            <button
                                              type="button"
                                              className={`rom-child-toggle${isExpanded ? " open" : ""}`}
                                              onClick={() => toggleFatherExpand(it.submenuId)}
                                              title={isExpanded ? "Collapse sub-menus" : "Expand sub-menus"}
                                            >
                                              <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
                                            </button>
                                          ) : (
                                            <span className="rom-menu-spacer" />
                                          )}
                                          {it.submenuName}
                                          {hasChildren && <span className="rom-child-count">{it.children.length}</span>}
                                        </td>
                                        {ACTIONS.map((a) => {
                                          if (!it.actions.includes(a.key)) {
                                            return <td className="rom-act" key={a.key}><span className="rom-dash">—</span></td>;
                                          }
                                          const on = curRights[it.submenuId][a.key];
                                          return (
                                            <td className="rom-act" key={a.key}>
                                              <input
                                                type="checkbox"
                                                className={`rom-perm${a.key === "view" ? " rom-pv" : ""}`}
                                                checked={!!on}
                                                onChange={(e) => setCell(it.submenuId, a.key, e.target.checked)}
                                              />
                                            </td>
                                          );
                                        })}
                                      </tr>
                                      {hasChildren && isExpanded && it.children.map((ch, ci) => (
                                        <tr
                                          className={`rom-child-row${ci === it.children.length - 1 ? " rom-child-last" : ""}`}
                                          key={ch.submenuId}
                                        >
                                          <td className="rom-menu">
                                            <span className="rom-child-name">{ch.submenuName}</span>
                                          </td>
                                          {ACTIONS.map((a) => {
                                            if (!ch.actions.includes(a.key)) {
                                              return <td className="rom-act" key={a.key}><span className="rom-dash">—</span></td>;
                                            }
                                            const on = curRights[ch.submenuId][a.key];
                                            return (
                                              <td className="rom-act" key={a.key}>
                                                <input
                                                  type="checkbox"
                                                  className={`rom-perm${a.key === "view" ? " rom-pv" : ""}`}
                                                  checked={!!on}
                                                  onChange={(e) => setCell(ch.submenuId, a.key, e.target.checked)}
                                                />
                                              </td>
                                            );
                                          })}
                                        </tr>
                                      ))}
                                    </Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ROLE MODAL */}
      <div className={`rom-modal-overlay${modalOpen ? " rom-show" : ""}`} onMouseDown={(e) => { if (e.target === e.currentTarget) closeRoleModal(); }}>
        <div className="rom-modal">
          <div className="rom-modal-head">
            <div className="rom-mh-ico"><IcShield /></div>
            <div className="rom-mh-title">{editRoleId ? "Edit Role" : "Create Role"}</div>
            <button className="rom-modal-x" onClick={closeRoleModal}>✕</button>
          </div>
          <div className="rom-modal-body">
            <div className="rom-field">
              <label>Role Name <span className="rom-req">*</span></label>
              <input
                type="text"
                placeholder="e.g. Branch Manager"
                className={nameErr ? "rom-err" : ""}
                value={formName}
                onChange={(e) => { setFormName(e.target.value); if (nameErr) setNameErr(false); }}
              />
              <div className={`rom-field-err${nameErr ? " rom-show" : ""}`}>Role name is required</div>
            </div>
            <div className="rom-field">
              <label>Description</label>
              <input
                type="text"
                placeholder="Short description of this role"
                value={formDesc}
                onChange={(e) => setFormDesc(e.target.value)}
              />
            </div>
            {!editRoleId && (
              <div className="rom-field">
                <label>Copy permissions from</label>
                <select value={formCopyId} onChange={(e) => setFormCopyId(e.target.value)}>
                  <option value="">— Start blank (no access) —</option>
                  {roles.map((x) => <option key={x.roleId} value={x.roleId}>{x.roleName}</option>)}
                </select>
              </div>
            )}
            <div className="rom-tog-row">
              <div><div className="rom-tog-name">Active</div></div>
              <label className="rom-switch">
                <input type="checkbox" checked={formActive} onChange={(e) => setFormActive(e.target.checked)} />
                <span className="rom-slider"></span>
              </label>
            </div>
          </div>
          <div className="rom-modal-foot">
            <button className="rom-btn rom-btn-ghost" onClick={closeRoleModal} disabled={roleBusy}>Cancel</button>
            <button className="rom-btn rom-btn-green" onClick={saveRole} disabled={roleBusy}>
              <IcCheck /> {roleBusy ? "Saving…" : "Save Role"}
            </button>
          </div>
        </div>
      </div>

      <div className="rom-toast-wrap">
        {toasts.map((t) => {
          const icons = { ok: "✓", err: "✕", warn: "⚠", info: "ℹ" };
          return (
            <div className={`rom-toast rom-${t.type || "info"}`} key={t.id}>
              <span style={{ fontSize: 15 }}>{icons[t.type] || "ℹ"}</span>{t.msg}
            </div>
          );
        })}
      </div>
    </div>
  );
}